// A lab report's proposal merged into the open Test form (TGCLOUD-1866, Phase 2 rework: "the Test
// record is the home of the result"). Pure: no React, no requests.
//
// POST /test-results/intake answers with a proposal in the Test form's shape (backend
// services/testResults/proposal.js). Since TGCLOUD-1868 (plan D3) it is merged into the form the
// moment the reading arrives, by these rules, and each part of it is then confirmed where it is on
// the form. Merging changes only the FORM: nothing is written until the clinician saves the Test,
// and that save (PUT /clinical-notes/test with labReportApplication) is what records the report,
// its provenance and the genotype copy. Closing without saving discards it.
//
// A CONTRADICTION IS CHOSEN ON THE FORM (withChoice): the Test keeps its value until the clinician
// takes the report's, and can take it back. A report dismissed, or sent to the lab report inbox,
// comes off the form again (unapplyReport).
//
// THE RULES (plan, final section, "Existing data"):
//   - A Test field the form holds EMPTY is filled from the report. One that holds a DIFFERENT
//     value is a conflict: both are shown and the clinician chooses, per field. The same value is
//     left alone. Nothing the report does not give is touched.
//   - Genes tested are merged by gene (its key), variants by what they are (the same finding, or
//     the same gene and description: testRecordModel.sameVariant; where the report's description
//     was withheld for want of confidence, the Test's matched to what was read by digest,
//     `matched`), so nothing is ever listed twice. A gene or variant the Test does not hold is
//     added. One it holds is:
//       same      nothing differs: kept exactly as it is;
//       filled    (a gene row) the Test's row states nothing beyond the gene, as "Add genes"
//                 makes one: the report's row replaces it, since there is nothing to contradict;
//       merged    (a variant) nothing contradicts and the report adds details the Test's variant
//                 lacks: they are added, and the variant becomes the report's (labReportId,
//                 findingId), since its description is now the report's;
//       conflict  a detail both give differs: the clinician chooses the Test's or the report's.
//                 For a variant, "the report's" is the report's side of EACH DIFFERENCE, with what
//                 the Test lacked filled from the report, never the report's variant whole: what
//                 the clinician entered that the report does not contradict (an origin the report
//                 does not state, the date last reviewed) is not the choice they were shown.
//   - An item from the report keeps its labReportId and findingId, including once it is edited:
//     the save compares it with the reading and records what the clinician changed.
//
// THE DATE RULE the save applies (backend applyLabReport.applyRequestDateRule), mirrored here so
// the form never shows a result received before it was requested:
//   - the report's date fills an empty Date Result Received, unless it is BEFORE a date requested
//     the clinician gave: then it is not filled, and the review says why;
//   - when the date requested is still the default of a new Test (the day the form opened), the
//     report's date is filled even though it is earlier, and SAVING clears the default date
//     requested (requestDateIsDefault). The review says that too.
// Days are compared as calendar days, each read by how it is written (CLAUDE.md, Dates): the
// report's date is UTC midnight of its day, the date requested a picker's local instant or an
// importer's UTC midnight: both toLocalDateFromPickerOrImport.

import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';
import {
  geneKeyOf,
  hgvsKeyOf,
  rowKeyOf,
  sameVariant
} from '../clinical-notes-section/forms/test-record/testRecordModel';

export const FIELDS = Object.freeze([
  'testName',
  'lab',
  'dateResultReceived',
  'testCode',
  'resultSummaryAsPrinted',
  'testResult',
  'testScope'
]);
export const FIELD_LABELS = Object.freeze({
  testName: 'Test Name',
  lab: 'Laboratory',
  dateResultReceived: 'Date Result Received',
  testCode: 'Test Code',
  resultSummaryAsPrinted: 'Result summary, as printed',
  testResult: 'Test Result',
  testScope: 'Test scope',
  // Not a field the report fills: a withdrawal gives it back (TGCLOUD-1868 B2).
  genesTestedList: 'Gene list'
});

// A plain object: never a Date (which has no keys of its own, and is not blank).
const isPlain = (v) => {
  if (v === null || typeof v !== 'object') return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
};
const text = (v) => (typeof v === 'string' ? v.trim() : '');
const isBlank = (v) =>
  v === undefined ||
  v === null ||
  (typeof v === 'string' && v.trim() === '') ||
  (isPlain(v) && Object.keys(v).length === 0);

/** A date as its calendar day, 20260911, or null. */
export function dayOf(value) {
  const d = toLocalDateFromPickerOrImport(value);
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

// What the report proposes for a field. Test Result is the group's own label for the report's
// conclusion (testResultLabel.js), or nothing.
const proposedValue = (proposal, field) =>
  field === 'testResult'
    ? proposal?.testResultSuggestion?.label || null
    : proposal?.[field] ?? null;

function sameField(field, a, b) {
  if (isBlank(a) && isBlank(b)) return true;
  if (isBlank(a) || isBlank(b)) return false;
  if (field === 'dateResultReceived') return dayOf(a) === dayOf(b);
  if (field === 'testScope') {
    return a.kind === b.kind && text(a.asPrinted) === text(b.asPrinted);
  }
  // What the gene rows are said to be (TGCLOUD-1868 B2): its state, which a withdrawal gives back.
  if (field === 'genesTestedList') return text(a.status) === text(b.status);
  return text(a) === text(b);
}

// ── Genes tested ──────────────────────────────────────────────────────────────────────────────

const GENE_DETAILS = Object.freeze([
  ['Scope', (r) => r.scope || 'whole-gene'],
  ['Region studied', (r) => text(r.regionDescriptionAsPrinted)],
  ['Transcript', (r) => r.transcript || ''],
  [
    'Methods',
    (r) =>
      JSON.stringify((r.methods || []).map((m) => [m.kind, text(m.asPrinted)]))
  ],
  ['Coverage', (r) => r.coverage?.status || 'not-stated'],
  ['Coverage, as printed', (r) => text(r.coverage?.asPrinted)]
]);

/** A row that says nothing beyond its gene: what "Add genes" makes. */
export const isBareGeneRow = (row) =>
  GENE_DETAILS.every(
    ([, read]) =>
      read(row) === read({ scope: 'whole-gene', coverage: {}, methods: [] })
  );

const geneDifferences = (current, proposed) =>
  GENE_DETAILS.filter(([, read]) => read(current) !== read(proposed)).map(
    ([label]) => label
  );

// ── Variants ──────────────────────────────────────────────────────────────────────────────────

const statedOf = (v) => (v && v.value !== 'not-stated' ? v.value || '' : '');
const VARIANT_DETAILS = Object.freeze([
  ['c.', 'variantC', (v) => hgvsKeyOf(v.variantC, 'c.')],
  ['g.', 'variantG', (v) => hgvsKeyOf(v.variantG, 'g.')],
  ['p.', 'variantP', (v) => hgvsKeyOf(v.variantP, 'p.')],
  [
    'Variant Interpretation',
    'variantInterpretation',
    (v) => text(v.variantInterpretation)
  ],
  ['Origin', 'origin', (v) => (v.origin === 'unknown' ? '' : v.origin || '')],
  ['Transcript', 'transcript', (v) => v.transcript || ''],
  ['Genome build', 'genomeBuild', (v) => v.genomeBuild || ''],
  ['Zygosity', 'zygosity', (v) => statedOf(v.zygosity)],
  ['Inheritance', 'inheritance', (v) => statedOf(v.inheritance)],
  [
    'Laboratory classification',
    'labClassification',
    (v) => text(v.labClassification?.asPrinted)
  ]
]);

// What each side gives: `fills` the keys the report gives and the Test lacks, `conflicts` the
// labels shown for the details both give that differ, `conflictKeys` their keys.
function compareVariant(current, proposed) {
  const fills = [];
  const conflicts = [];
  const conflictKeys = [];
  for (const [label, key, read] of VARIANT_DETAILS) {
    const was = read(current);
    const now = read(proposed);
    if (!now || was === now) continue;
    if (!was) fills.push(key);
    else {
      conflicts.push(label);
      conflictKeys.push(key);
    }
  }
  return { fills, conflicts, conflictKeys };
}

// The Test's variant with what the report adds: every detail the Test's variant lacks, the lab's
// printed descriptions, and the report's ids. Nothing the Test's variant gives is changed.
function mergeVariant(current, proposed, fills) {
  const merged = { ...current };
  for (const key of fills) merged[key] = proposed[key];
  if (isBlank(merged.hgvsAsPrinted) && !isBlank(proposed.hgvsAsPrinted)) {
    merged.hgvsAsPrinted = proposed.hgvsAsPrinted;
  }
  merged.labReportId = proposed.labReportId;
  merged.findingId = proposed.findingId;
  return merged;
}

// "Use the report's" on a variant: the merge, and the report's value for each detail shown as a
// difference (conflictKeys), with the lab's printed descriptions. Nothing else the Test's variant
// gives is touched: the choice the clinician made was about the differences they were shown.
function withReportsSide(current, proposed, fills, conflictKeys) {
  const merged = mergeVariant(current, proposed, fills);
  for (const key of conflictKeys) merged[key] = proposed[key];
  if (!isBlank(proposed.hgvsAsPrinted)) {
    merged.hgvsAsPrinted = proposed.hgvsAsPrinted;
  }
  return merged;
}

// ── What the reader was not confident of (TGCLOUD-1868, plan B1 and D3) ─────────────────────────
//
// A value the reader was not confident of ARRIVES EMPTY, to be typed or chosen from the report,
// never confirmed with a click. The answer's proposal holds null there, but the Test may already
// hold a value at the same place: a Test Result set when the test was requested, or from a first
// report before an amended one; the genome build of a variant entered by hand that the report's
// finding is merged into. Kept, that value would satisfy the group with one Confirm, compared with
// nothing. So the application empties it too, and keeps what the Test held: the review says so
// ("This Test held: …"), and taking the report off gives it back.

const getAt = (object, path) =>
  String(path || '')
    .split('.')
    .reduce((v, key) => (v == null ? undefined : v[key]), object);
// `object` with `value` at `path`, copied along the path; everything else shared.
const setAt = (object, path, value) => {
  const [head, ...rest] = String(path).split('.');
  const base = isPlain(object) ? object : {};
  return {
    ...base,
    [head]: rest.length ? setAt(base[head], rest.join('.'), value) : value
  };
};

// The reading's withheld paths (the answer's `lowConfidence`), by where they land on the form: the
// Test Result, and each variant's own fields by the variant's place in the proposal.
function withheldPlaces(withheld) {
  const out = { testResult: false, variants: new Map() };
  for (const path of Array.isArray(withheld) ? withheld : []) {
    if (typeof path !== 'string') continue;
    if (path === 'testResultSuggestion') {
      out.testResult = true;
      continue;
    }
    const m = /^variants\.(\d+)\.(.+)$/.exec(path);
    if (m) {
      const at = Number(m[1]);
      out.variants.set(at, [...(out.variants.get(at) || []), m[2]]);
    }
  }
  return out;
}

// A variant as the application leaves it, with every withheld value it holds emptied, and what was
// emptied: [{ field, value }].
function emptiedOf(variant, fields) {
  let out = variant;
  const cleared = [];
  for (const field of fields) {
    const value = getAt(out, field);
    if (isBlank(value)) continue;
    out = setAt(out, field, null);
    cleared.push({ field, value });
  }
  return { variant: out, cleared };
}

// ── The plan ──────────────────────────────────────────────────────────────────────────────────

/** An item's id in `choices`: 'field:lab', 'gene:BRCA2', 'variant:<findingId>'. */
export const choiceId = (kind, key) => `${kind}:${key}`;

/**
 * What applying `proposal` to `form` would do, item by item, before anything is applied.
 *
 * @param {object} args
 * @param {object} args.form       the Test form's values now
 * @param {object} args.proposal   the report's proposal, as the review has completed it
 * @param {boolean} args.requestDateIsDefault  the date requested is still a new Test's default
 * @param {string[]} [args.withheld]  the reading's paths it was not confident of (the answer's
 *   `lowConfidence`): emptied on the form wherever it holds a value (see above). The Test Result
 *   is a `withheld` item whatever the Test held, so taking the report off gives back what it held
 *   in place of what was chosen from the report
 * @param {Map<string, number>} [args.matched]  findingId -> the index of the Test's variant that
 *   finding is, where its description was withheld and so cannot be compared here
 *   (lab-report-verification/withheldMatches.js, by digest). Used only where the variant there is
 *   still of the finding's gene and no finding's own
 * @returns {{
 *   fields: Array<{ id, field, kind: 'fill'|'same'|'conflict'|'skipped'|'withheld', current,
 *     proposed, note? }>,
 *   genes: Array<{ id, key, kind: 'add'|'same'|'fill'|'conflict', current?, proposed,
 *     differences? }>,
 *   variants: Array<{ id, findingId, kind: 'add'|'same'|'merge'|'conflict', index?, current?,
 *     proposed, merged?, differences?, withReport?, cleared?: Array<{ field, value }> }>
 * }}
 */
export function planApplication({
  form,
  proposal,
  requestDateIsDefault,
  withheld = [],
  matched = null
}) {
  const places = withheldPlaces(withheld);
  const fields = [];
  for (const field of FIELDS) {
    if (field === 'testResult' && places.testResult) {
      fields.push({
        id: choiceId('field', field),
        field,
        kind: 'withheld',
        current: form?.[field],
        proposed: null
      });
      continue;
    }
    const proposed = proposedValue(proposal, field);
    if (isBlank(proposed)) continue;
    const current = form?.[field];
    const id = choiceId('field', field);
    if (sameField(field, current, proposed)) {
      fields.push({ id, field, kind: 'same', current, proposed });
      continue;
    }
    if (!isBlank(current)) {
      fields.push({ id, field, kind: 'conflict', current, proposed });
      continue;
    }
    if (field === 'dateResultReceived') {
      const reportDay = dayOf(proposed);
      const requestDay = dayOf(form?.dateRequested);
      if (reportDay && requestDay && reportDay < requestDay) {
        if (requestDateIsDefault) {
          fields.push({
            id,
            field,
            kind: 'fill',
            current,
            proposed,
            note: 'The report is dated before the date requested, which is only this new Test’s default: saving clears the date requested.'
          });
        } else {
          fields.push({
            id,
            field,
            kind: 'skipped',
            current,
            proposed,
            note: 'Not filled: the report is dated before the date requested.'
          });
        }
        continue;
      }
    }
    fields.push({ id, field, kind: 'fill', current, proposed });
  }

  const currentGenes = Array.isArray(form?.genesTested) ? form.genesTested : [];
  const genes = (proposal?.genesTested || []).map((proposed) => {
    const key = rowKeyOf(proposed);
    const id = choiceId('gene', key);
    const current = currentGenes.find((r) => rowKeyOf(r) === key);
    if (!current) return { id, key, kind: 'add', proposed };
    const differences = geneDifferences(current, proposed);
    if (!differences.length)
      return { id, key, kind: 'same', current, proposed };
    if (isBareGeneRow(current)) {
      return { id, key, kind: 'fill', current, proposed, differences };
    }
    return { id, key, kind: 'conflict', current, proposed, differences };
  });

  const currentVariants = Array.isArray(form?.variants) ? form.variants : [];
  const taken = new Set();
  // The Test's variant a finding is by digest, while it is still one it could be.
  const matchedIndexOf = (proposed) => {
    const i =
      matched instanceof Map ? matched.get(proposed.findingId) : undefined;
    const v = Number.isInteger(i) ? currentVariants[i] : null;
    return v &&
      !taken.has(i) &&
      !v.findingId &&
      geneKeyOf(v.gene) &&
      geneKeyOf(v.gene) === geneKeyOf(proposed.gene)
      ? i
      : -1;
  };
  const variants = (proposal?.variants || []).map((proposed, at) => {
    const id = choiceId('variant', proposed.findingId);
    const found = currentVariants.findIndex(
      (v, i) => !taken.has(i) && sameVariant(v, proposed)
    );
    const index = found >= 0 ? found : matchedIndexOf(proposed);
    if (index < 0) {
      return { id, findingId: proposed.findingId, kind: 'add', proposed };
    }
    taken.add(index);
    const current = currentVariants[index];
    const { fills, conflicts, conflictKeys } = compareVariant(
      current,
      proposed
    );
    const base = {
      id,
      findingId: proposed.findingId,
      index,
      current,
      proposed
    };
    // What the Test's variant holds where the reader was not confident is emptied (see above).
    const withheldFields = places.variants.get(at) || [];
    if (conflicts.length) {
      const { variant: withReport, cleared } = emptiedOf(
        withReportsSide(current, proposed, fills, conflictKeys),
        withheldFields
      );
      return {
        ...base,
        kind: 'conflict',
        differences: conflicts,
        // What "Use the report's" puts on the Test.
        withReport,
        ...(cleared.length ? { cleared } : {})
      };
    }
    const { variant: merged, cleared } = emptiedOf(
      mergeVariant(current, proposed, fills),
      withheldFields
    );
    if (fills.length || cleared.length) {
      return {
        ...base,
        kind: 'merge',
        merged,
        ...(cleared.length ? { cleared } : {})
      };
    }
    return { ...base, kind: 'same' };
  });

  return { fields, genes, variants };
}

/** Every conflict in a plan, in the order shown. */
export const conflictsOf = (plan) =>
  [...plan.fields, ...plan.genes, ...plan.variants].filter(
    (item) => item.kind === 'conflict'
  );

// The report's row in place of the Test's: the gene's ids the Test already had stay where the report
// has none.
const reportGeneRow = (item) => ({
  ...item.proposed,
  gene: {
    ...item.proposed.gene,
    hgncId: item.proposed.gene?.hgncId || item.current?.gene?.hgncId || null,
    ncbiGeneId:
      item.proposed.gene?.ncbiGeneId || item.current?.gene?.ncbiGeneId || null
  }
});

/**
 * The form with the plan applied, and which values came from the report (for "from the report").
 *
 * @param {object} form
 * @param {object} plan     planApplication's answer, for this form
 * @param {Object<string, 'report'|'current'>} choices  per conflict id
 * @returns {{ form: object, applied: { fields: Set<string>, genes: Set<string>,
 *   findings: Set<string> } }}
 */
export function applyPlan(form, plan, choices = {}) {
  const next = { ...form };
  const applied = {
    fields: new Set(),
    genes: new Set(),
    findings: new Set()
  };
  const takesReport = (item) =>
    item.kind === 'conflict' && choices[item.id] === 'report';

  for (const item of plan.fields) {
    // Emptied for the clinician to choose from the report: not the report's value.
    if (item.kind === 'withheld') {
      if (!isBlank(next[item.field])) next[item.field] = '';
      continue;
    }
    if (item.kind === 'fill' || takesReport(item)) {
      next[item.field] = item.proposed;
      applied.fields.add(item.field);
    }
  }

  let genes = Array.isArray(form.genesTested) ? [...form.genesTested] : null;
  for (const item of plan.genes) {
    if (item.kind === 'add') {
      genes = [...(genes || []), item.proposed];
      applied.genes.add(item.key);
    } else if (item.kind === 'fill' || takesReport(item)) {
      const proposed = reportGeneRow(item);
      genes = genes.map((r) => (rowKeyOf(r) === item.key ? proposed : r));
      applied.genes.add(item.key);
    }
  }
  if (applied.genes.size) next.genesTested = genes;

  let variants = Array.isArray(form.variants) ? [...form.variants] : [];
  let variantsChanged = false;
  for (const item of plan.variants) {
    if (item.kind === 'add') {
      variants = [...variants, item.proposed];
    } else if (item.kind === 'merge') {
      variants[item.index] = item.merged;
    } else if (takesReport(item)) {
      variants[item.index] = item.withReport || item.proposed;
    } else {
      continue;
    }
    variantsChanged = true;
    applied.findings.add(item.findingId);
  }
  if (variantsChanged) next.variants = variants;

  return { form: next, applied };
}

// ── A contradiction chosen, and a report taken off again (TGCLOUD-1868 D3) ───────────────────────

const sameJson = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const fromReport = (row, labReportId) =>
  !!row &&
  row.labReportId != null &&
  labReportId != null &&
  String(row.labReportId) === String(labReportId);

/**
 * The form with one contradiction of the plan settled: `choice` 'report' takes the report's side
 * (for a variant, the report's side of each difference with what the Test lacked: `withReport`),
 * 'current' gives the Test's own value back. Chosen again, it changes again. A gene row is found
 * by its key; a variant by the finding it became, else the Test's own variant as it was.
 *
 * @param {object} form    the form's values now
 * @param {object} item    a conflict of the plan made when the report was applied
 * @param {'report'|'current'} choice
 * @returns {object} the form (the same object when there is nothing to change)
 */
export function withChoice(form, item, choice) {
  if (!item || item.kind !== 'conflict') return form;
  const report = choice === 'report';
  if (item.field) {
    const value = report ? item.proposed : item.current;
    return sameField(item.field, form?.[item.field], value)
      ? form
      : { ...form, [item.field]: value };
  }
  if (item.key) {
    const rows = Array.isArray(form?.genesTested) ? form.genesTested : [];
    const at = rows.findIndex((r) => rowKeyOf(r) === item.key);
    if (at < 0) return form;
    const list = [...rows];
    list[at] = report ? reportGeneRow(item) : item.current;
    return { ...form, genesTested: list };
  }
  const variants = Array.isArray(form?.variants) ? form.variants : [];
  const at = variants.findIndex(
    (v) =>
      !!v &&
      ((item.findingId && v.findingId === item.findingId) ||
        v === item.current ||
        sameJson(v, item.current))
  );
  if (at < 0) return form;
  const list = [...variants];
  list[at] = report ? item.withReport || item.proposed : item.current;
  return { ...form, variants: list };
}

/**
 * The form with a report taken off it again: dismissed, or sent to the lab report inbox
 * (TGCLOUD-1868: a reading is applied the moment it arrives, so leaving it means undoing that).
 *
 * What the report ADDED goes: its gene rows and variants, found by the report's id on them, typed
 * values included (they completed the report's own). What it REPLACED comes back as the Test held
 * it: a field it filled or was chosen for (where the form still holds the report's value; one the
 * clinician has changed since is theirs and stays), a gene row it filled, a variant it was merged
 * into or chosen over. A value the application emptied because the reader was not confident of it
 * (the Test Result) comes back as the Test held it, whatever was chosen there since: it was chosen
 * from this report. The list state the application proposed is taken back where the form still
 * holds it.
 *
 * WHAT WAS ENTERED FOR THE REPORT goes too (`entered`, TGCLOUD-1868, review of the view): the
 * view has the clinician copy the report into the form by hand where the reader was not confident,
 * with no report id to find it by. A gene row added while the report was applied, of a gene the
 * Test did not list before, goes (unless a variant that stays on the Test is in it), and a list
 * state chosen while it was applied gives way to the one the Test held. Sent to the inbox as
 * someone else's report, nothing of it may stay on this person's Test.
 *
 * Everything else the clinician added by hand stays.
 *
 * @param {object} form
 * @param {{ plan: object, choices?: object, labReportId: string, before?: object,
 *   listState?: object|null, entered?: { genes?: string[], listState?: boolean } }} application
 *   the plan made when the report was applied, the choices made since, the report's id, and what
 *   the form held before for genesTested and genesTestedList (`before`); `listState` the list
 *   state the application put on the form; `entered` the gene keys added and whether the list
 *   state was chosen while it was applied
 * @returns {object}
 */
export function unapplyReport(
  form,
  {
    plan,
    choices = {},
    labReportId,
    before = {},
    listState = null,
    entered = null
  } = {}
) {
  if (!plan) return form;
  const next = { ...form };
  const tookReport = (item) =>
    item.kind === 'fill' ||
    (item.kind === 'conflict' && choices[item.id] === 'report');

  for (const item of plan.fields || []) {
    // A value the reader was not confident of was emptied for the clinician to choose FROM THE
    // REPORT: whatever was chosen there goes with it, and the Test gets back what it held.
    const restores =
      item.kind === 'withheld' ||
      (tookReport(item) &&
        sameField(item.field, form?.[item.field], item.proposed));
    if (restores) {
      // Exactly as the form held it: a field it never held is left off again.
      if (item.current === undefined) delete next[item.field];
      else next[item.field] = item.current;
    }
  }
  if (
    (listState &&
      sameField('genesTestedList', form?.genesTestedList, listState)) ||
    entered?.listState === true
  ) {
    if (before.genesTestedList === undefined) delete next.genesTestedList;
    else next.genesTestedList = before.genesTestedList;
  }

  if (Array.isArray(form?.variants)) {
    let variants = [...form.variants];
    for (const item of plan.variants || []) {
      const isTheFinding = (v) =>
        !!v && v.findingId === item.findingId && fromReport(v, labReportId);
      if (item.kind === 'add') {
        variants = variants.filter((v) => !isTheFinding(v));
      } else if (item.kind === 'merge' || tookReport(item)) {
        variants = variants.map((v) => (isTheFinding(v) ? item.current : v));
      }
    }
    if (!sameJson(variants, form.variants)) next.variants = variants;
  }

  if (Array.isArray(form?.genesTested)) {
    let rows = [...form.genesTested];
    for (const item of plan.genes || []) {
      if (item.kind === 'add') {
        rows = rows.filter(
          (r) => !(rowKeyOf(r) === item.key && fromReport(r, labReportId))
        );
      } else if (item.kind === 'fill' || tookReport(item)) {
        rows = rows.map((r) =>
          rowKeyOf(r) === item.key && fromReport(r, labReportId)
            ? item.current
            : r
        );
      }
    }
    // A gene entered while the report was applied, the Test not listing it before, goes with the
    // report, unless a variant staying on the Test is in it.
    const enteredGenes = new Set(
      Array.isArray(entered?.genes) ? entered.genes : []
    );
    if (enteredGenes.size) {
      const listedBefore = new Set(
        (Array.isArray(before.genesTested) ? before.genesTested : []).map(
          rowKeyOf
        )
      );
      const inAVariant = new Set(
        (Array.isArray(next.variants) ? next.variants : [])
          .map((v) => geneKeyOf(v?.gene))
          .filter(Boolean)
      );
      rows = rows.filter((r) => {
        const key = rowKeyOf(r);
        return (
          !enteredGenes.has(key) || listedBefore.has(key) || inAVariant.has(key)
        );
      });
    }
    if (!sameJson(rows, form.genesTested)) {
      if (!rows.length && before.genesTested === undefined) {
        delete next.genesTested;
      } else {
        next.genesTested = rows;
      }
    }
  }
  return next;
}

/**
 * The Test fields a report filled, or was chosen for, that the clinician changed since: taking the
 * report off leaves them as changed (unapplyReport), so whoever takes it off is told to check them.
 *
 * @returns {string[]} the fields, in the plan's order
 */
export function changedAfterTheReport(form, { plan, choices = {} } = {}) {
  if (!plan) return [];
  return (plan.fields || [])
    .filter(
      (item) =>
        (item.kind === 'fill' ||
          (item.kind === 'conflict' && choices[item.id] === 'report')) &&
        !sameField(item.field, form?.[item.field], item.proposed) &&
        !sameField(item.field, form?.[item.field], item.current)
    )
    .map((item) => item.field);
}

// ── Before saving ─────────────────────────────────────────────────────────────────────────────

// A reading that would put on the Test something the report does not say: a positive with no
// variant read, a negative against no gene. Saving waits for the clinician to say they have
// checked, as it does for a wrong-patient report (verificationGroups.saveBlockers). The words are
// kept as they were when the report was applied by a click (TGCLOUD-1868 plan D, "What must
// survive").
export const READING_WARNINGS = Object.freeze({
  'findings-not-structured': {
    text: 'The report’s conclusion says a variant was found, but no variant was read from it. Saving records no variant from this report: check its findings, and see “Not read from this report”.',
    blocker:
      'Acknowledge that the finding the report’s conclusion names was not read.'
  },
  'no-genes': {
    text: 'No genes tested were read from this report, so this negative result will list no gene as tested.',
    blocker: 'Acknowledge that this negative result lists no gene as tested.'
  }
});

/**
 * The READING_WARNINGS keys that apply to a proposal, in the order shown. Given the reading's gene
 * list (`geneList`, TGCLOUD-1868 B2), "no genes" is a list of which no gene was READ: one withheld
 * for entry by hand was read, and its card asks for the genes and what the list is instead.
 */
export function readingWarnings(proposal, { geneList = null } = {}) {
  const conclusion = proposal?.testResultSuggestion?.conclusion;
  const out = [];
  if (
    (conclusion === 'positive' || conclusion === 'inconclusive') &&
    !(proposal?.variants || []).length
  ) {
    out.push('findings-not-structured');
  }
  const noGeneRead =
    geneList && Number.isFinite(geneList.readCount)
      ? geneList.readCount === 0
      : !(proposal?.genesTested || []).length;
  if (conclusion === 'negative' && noGeneRead) {
    out.push('no-genes');
  }
  return out;
}

/**
 * The READING_WARNINGS keys an intake (or hand-off) answer raises. The server works them out from
 * the WHOLE reading and sends them (`readingWarnings`, backend services/testResults/
 * readingWarnings.js): the proposal shown here withholds the conclusion whenever the reader was not
 * confident of it, and readingWarnings over it then raised nothing, a positive with no variant read
 * saved with nothing acknowledged (TGCLOUD-1868, review of the view). An answer that carries none
 * is read the old way, from its proposal.
 */
export function warningsOfReading(reading) {
  if (Array.isArray(reading?.readingWarnings)) {
    return reading.readingWarnings.filter(
      (key, i, all) =>
        typeof key === 'string' &&
        Object.prototype.hasOwnProperty.call(READING_WARNINGS, key) &&
        all.indexOf(key) === i
    );
  }
  return readingWarnings(reading?.proposal, { geneList: reading?.geneList });
}

/** "MLH1 c.1852_1854del": a proposal variant as a clinician names it. */
export const variantLabel = (v) =>
  [v?.gene, v?.variantC || v?.variantG || v?.variantP]
    .filter(Boolean)
    .join(' ');

/**
 * What an incomplete item is:
 *   build        a genome build the report does not state: chosen on its variant, from a
 *                GRCh37/GRCh38 select with NO default (it is a low-confidence value of the
 *                variant's identity, TGCLOUD-1868)
 *   transcript   a versioned accession the report lacks: typed on its variant, likewise
 *   names        text that names the patient: said, for the clinician to edit on the Test
 *   note         anything else: said, for the clinician to complete in the form
 */
export function incompleteKind(item) {
  if (item?.code === 'NAMES_PATIENT') return 'names';
  if (/^variants\.\d+\.genomeBuild$/.test(item?.path || '')) return 'build';
  if (/^variants\.\d+\.transcript$/.test(item?.path || '')) return 'transcript';
  return 'note';
}

// ── After a withdrawal ────────────────────────────────────────────────────────────────────────

const namesReport = (row, labReportId) =>
  row?.labReportId != null && String(row.labReportId) === String(labReportId);

/**
 * The gene rows and variants of the open form that name `labReportId` and are not as the Test was
 * loaded (`stored`): changed, or added, since. Withdrawing that report takes the form's rows from it
 * back to what the server leaves (withdrawalOntoForm), changes and all, so the dialog says which
 * first: saving the Test before the withdrawal keeps them, for it to name for review.
 *
 * @returns {Array<{ kind: 'gene', gene } | { kind: 'variant', gene, variantC, findingId }>}
 */
export function unsavedRowsFromReport(form, stored = {}, labReportId) {
  const storedRows = Array.isArray(stored?.genesTested)
    ? stored.genesTested
    : [];
  const storedVariants = Array.isArray(stored?.variants) ? stored.variants : [];
  const out = [];
  for (const row of Array.isArray(form?.genesTested) ? form.genesTested : []) {
    if (!namesReport(row, labReportId)) continue;
    const was = storedRows.find((r) => rowKeyOf(r) === rowKeyOf(row));
    if (!was || !sameJson(was, row)) {
      out.push({ kind: 'gene', gene: rowKeyOf(row) });
    }
  }
  for (const v of Array.isArray(form?.variants) ? form.variants : []) {
    if (!namesReport(v, labReportId)) continue;
    const was = storedVariants.find((x) => x?.findingId === v.findingId);
    if (!was || !sameJson(was, v)) {
      out.push({
        kind: 'variant',
        gene: v.gene,
        variantC: v.variantC || '',
        findingId: v.findingId
      });
    }
  }
  return out;
}

/**
 * The open form after a report was withdrawn (POST /test-results/:id/entered-in-error). The server
 * has, on the stored Test, removed what came only from the report, GIVEN BACK what the report had
 * replaced (a variant it was merged into, a gene row it filled, a field chosen over the Test's), and
 * released the gene rows that stay from it (no report, coverage not stated). The form must do the
 * same, or its next Save writes the report's values back.
 *
 * A FIELD the form still holds as it was loaded (`stored`) takes the server's; one the clinician has
 * changed since stays, as theirs. But a GENE ROW OR VARIANT NAMING THE WITHDRAWN REPORT follows the
 * server whatever the form has done to it (review of the decisions, finding 4): the save refuses a
 * row naming a report entered in error unless the Test still holds it (testRecord.js,
 * ROW_NAMES_WITHDRAWN_REPORT), so an edited copy kept here made the form unsaveable and lost every
 * unsaved change with it. A variant the server still holds under the report (one changed before,
 * left for review) is kept as the form has it. A gene row the server holds none of is released as
 * the server releases one. The dialog says first what this takes back (unsavedRowsFromReport).
 *
 * @param {object} form      the form's values now
 * @param {{ removed?: { fields?, genesTested?, variants? },
 *   restored?: { fields?, genesTested?: Array<{ gene, row }>,
 *     variants?: Array<{ findingId, row }> },
 *   detached?: { genesTested?: Array<{ gene, row }> }, testNote?: object }} answer
 * @param {{ stored?: object, labReportId: string }} options  `stored` is the Test as loaded
 */
export function withdrawalOntoForm(form, answer, { stored = {}, labReportId }) {
  const next = { ...form };
  const removed = answer?.removed || {};
  const restored = answer?.restored || {};
  const detached = answer?.detached || {};
  const testNote = answer?.testNote || {};
  const ours = (row) => namesReport(row, labReportId);
  const storedRows = Array.isArray(stored.genesTested)
    ? stored.genesTested
    : [];
  const storedVariants = Array.isArray(stored.variants) ? stored.variants : [];
  // The form still holds the row exactly as it was loaded.
  const asLoadedRow = (row) => {
    const was = storedRows.find((r) => rowKeyOf(r) === rowKeyOf(row));
    return !!was && sameJson(was, row);
  };

  for (const field of removed.fields || []) {
    if (sameField(field, form[field], stored[field])) {
      next[field] = field in testNote ? testNote[field] : null;
    }
  }
  // The Test's own value, given back where the form still holds the report's as loaded.
  for (const field of restored.fields || []) {
    if (field in testNote && sameField(field, form[field], stored[field])) {
      next[field] = testNote[field];
    }
  }
  // THE GENE LIST'S STATE as the withdrawal left it, where the form still holds the state as loaded.
  // The server also changes it without giving it back or removing it (an exome's "complete" made
  // "only some", a count another live report still states keeping a state), naming it for review
  // only, so it is in neither list; the answer's Test (`testNote`) carries it whenever it changed.
  // Held as loaded, the form's next save wrote "complete" back, so every gene it does not list read
  // "not tested" again, or was refused as short (TGCLOUD-1868 review).
  if (
    'genesTestedList' in testNote &&
    sameField('genesTestedList', form.genesTestedList, stored.genesTestedList)
  ) {
    next.genesTestedList = testNote.genesTestedList;
  }

  if (Array.isArray(form.genesTested)) {
    const genes = new Set(removed.genesTested || []);
    // A row given back, or released from the report: the server's row replaces the form's.
    const replacing = new Map(
      [...(restored.genesTested || []), ...(detached.genesTested || [])]
        .filter((entry) => entry && entry.row)
        .map((entry) => [entry.gene, entry.row])
    );
    // A row naming a lab report that the server gave back or released follows the server; so does
    // one naming the withdrawn report itself, which the server holds under it no longer.
    const followsServer = (row) =>
      ours(row) || (row?.labReportId != null && replacing.has(rowKeyOf(row)));
    if (genes.size || replacing.size || form.genesTested.some(ours)) {
      next.genesTested = form.genesTested
        // Taken off by the server: off the form, changed or not.
        .filter((row) => !(genes.has(rowKeyOf(row)) && ours(row)))
        .map((row) => {
          const key = rowKeyOf(row);
          if (replacing.has(key) && (followsServer(row) || asLoadedRow(row))) {
            return replacing.get(key);
          }
          if (!ours(row)) return row;
          // Added in the form under the report: released, as the server releases a row that stays.
          const own = { ...row, coverage: { status: 'not-stated' } };
          delete own.labReportId;
          delete own.findingKeys;
          return own;
        });
    }
  }

  if (Array.isArray(form.variants)) {
    const findings = new Set((removed.variants || []).map((v) => v.findingId));
    const givenBack = new Map(
      (restored.variants || [])
        .filter((entry) => entry && entry.row)
        .map((entry) => [entry.findingId, entry.row])
    );
    // The findings the server's Test still holds under the report: its variants as the withdrawal
    // left them (`testNote.variants`, when it changed them), else as they were loaded.
    const heldAfter = new Set(
      (Array.isArray(testNote.variants) ? testNote.variants : storedVariants)
        .filter((v) => ours(v))
        .map((v) => v.findingId)
    );
    if (findings.size || givenBack.size || form.variants.some(ours)) {
      next.variants = form.variants
        .filter((v) => {
          if (!v || !ours(v)) return true;
          if (findings.has(v.findingId)) return false;
          return givenBack.has(v.findingId) || heldAfter.has(v.findingId);
        })
        .map((v) =>
          v && ours(v) && givenBack.has(v.findingId)
            ? givenBack.get(v.findingId)
            : v
        );
    }
  }
  return next;
}

// ── While a report is applied, until the save ─────────────────────────────────────────────────

// WHAT IS NOT SAVED YET (Tim's decisions, 2026-10-01). While a lab report is applied to the form
// and the Test is not saved yet (the save will carry labReportApplication), EVERYTHING on the form
// that differs from the Test as last saved is drawn as the data entry grid draws an unsaved change
// (gold): what the report filled, what the clinician changed after it, and what the clinician
// changed by hand that the report never touched. With no report applied nothing is drawn. The form
// decides that (TestForm); this says only what differs.

// A blank text and no text are the same; text is compared trimmed.
const textOf = (v) =>
  typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim();
// An age typed, worked out or stored: 45, '45' and ' 45 ' are one age.
const numberOf = (v) => {
  const t = textOf(v);
  return t === '' ? '' : String(Number(t));
};
// An associated note: its id, else a label other than None's. None and no note are the same.
const noteOf = (v) => {
  if (!v || typeof v !== 'object') return '';
  if (v._id) return `id:${String(v._id)}`;
  const label = textOf(v.label);
  return label && label !== 'none' ? `label:${label}` : '';
};
// A list compared by what it holds, never its order. Requested By holds user ids on the form and
// the staff members themselves in a save's payload; the attachments are file names.
const setOf = (list, read) =>
  JSON.stringify(
    (Array.isArray(list) ? list : []).map(read).filter(Boolean).sort()
  );
const personOf = (p) =>
  p && typeof p === 'object' ? textOf(p.userId || p._id) : textOf(p);

const SAME_BY = Object.freeze({
  text: (a, b) => textOf(a) === textOf(b),
  day: (a, b) => dayOf(a) === dayOf(b),
  number: (a, b) => numberOf(a) === numberOf(b),
  flag: (a, b) => !!a === !!b,
  note: (a, b) => noteOf(a) === noteOf(b),
  people: (a, b) => setOf(a, personOf) === setOf(b, personOf),
  names: (a, b) => setOf(a, textOf) === setOf(b, textOf),
  scope: (a, b) => sameField('testScope', a, b)
});

/**
 * Every field of the Test form that can be drawn as an unsaved change, and how each is compared:
 * by the form's own rules, dates by calendar day and text trimmed.
 */
export const TEST_FORM_FIELDS = Object.freeze({
  testName: 'text',
  testType: 'text',
  dateRequested: 'day',
  ageWhenTested: 'number',
  associatedConsent: 'note',
  requestedBy: 'people',
  lab: 'text',
  testCode: 'text',
  dateResultDue: 'day',
  dateResultReceived: 'day',
  testResult: 'text',
  resultSummaryAsPrinted: 'text',
  testScope: 'scope',
  testNotes: 'text',
  fileAttachments: 'names',
  showInFamily: 'flag',
  reminderEnabled: 'flag',
  reminderDate: 'day'
});

// What a gene row or a variant holds, as the form compares it: keys in any order, text trimmed, a
// blank (empty text, null, an empty list or object) as no key, and a date (a key named date..., or
// a Date) as its calendar day.
const held = (value, key = '') => {
  if (/^date/i.test(key) || value instanceof Date) {
    return dayOf(value) ?? undefined;
  }
  if (typeof value === 'string') return value.trim() || undefined;
  if (Array.isArray(value)) {
    return value.length ? value.map((v) => held(v) ?? null) : undefined;
  }
  if (isPlain(value)) {
    const out = {};
    for (const k of Object.keys(value).sort()) {
      const v = held(value[k], k);
      if (v !== undefined && v !== null) out[k] = v;
    }
    return Object.keys(out).length ? out : undefined;
  }
  return value ?? undefined;
};
const rowContent = (row) => JSON.stringify(held(row) ?? null);
// A variant's origin nobody gave is the editor's 'unknown', as the form's own comparisons read it.
const variantContent = (v) =>
  rowContent(
    v && typeof v === 'object'
      ? { ...v, origin: v.origin === 'unknown' ? undefined : v.origin }
      : v
  );

/**
 * What on the open Test form differs from the Test as last saved (as loaded, for a Test not saved
 * since). The form draws it as an unsaved change while a lab report is applied (Tim's decisions,
 * 2026-10-01).
 *
 *   fields    every TEST_FORM_FIELDS field whose value differs, by its own rule;
 *   genes     the keys of the gene rows the saved Test does not hold, or holds with something
 *             different (a row's key is the form's identity for it);
 *   variants  the INDEXES in form.variants of each variant the saved Test does not hold exactly.
 *             A variant is matched by what it holds, and each saved variant answers for one. Its
 *             findingId, when it has one, is part of what it holds. Its place in the list is no
 *             identity: it moves when another variant is removed.
 * A value put back to what the Test holds is no change. Something removed is not drawn, since it
 * is no longer on the form.
 *
 * @param {object} form   the form's values now
 * @param {object} saved  the Test as last saved
 * @returns {{ fields: Set<string>, genes: Set<string>, variants: Set<number> }}
 */
export function unsavedTestChanges({ form, saved = {} }) {
  const fields = new Set(
    Object.entries(TEST_FORM_FIELDS)
      .filter(([field, kind]) => !SAME_BY[kind](form?.[field], saved?.[field]))
      .map(([field]) => field)
  );

  const savedRows = new Map(
    (Array.isArray(saved?.genesTested) ? saved.genesTested : []).map((r) => [
      rowKeyOf(r),
      rowContent(r)
    ])
  );
  const genes = new Set(
    (Array.isArray(form?.genesTested) ? form.genesTested : [])
      .filter((r) => savedRows.get(rowKeyOf(r)) !== rowContent(r))
      .map(rowKeyOf)
  );

  const unclaimed = new Map();
  for (const v of Array.isArray(saved?.variants) ? saved.variants : []) {
    const content = variantContent(v);
    unclaimed.set(content, (unclaimed.get(content) || 0) + 1);
  }
  const variants = new Set();
  (Array.isArray(form?.variants) ? form.variants : []).forEach((v, index) => {
    const content = variantContent(v);
    const left = unclaimed.get(content) || 0;
    if (left > 0) unclaimed.set(content, left - 1);
    else variants.add(index);
  });

  return { fields, genes, variants };
}
